/**
 * Settings panel: adults set the home location for the weather card. Prop-free — the
 * integrator mounts <WeatherLocationPanel /> in Settings. The Worker is the real gate
 * (adults only); this panel is the courtesy UI.
 *
 * No geocoder by design — a plain lat/lng + label form. Text a person typed is NEVER lost
 * on a failed save: the fields keep their values so nothing has to be re-entered.
 */
import { useId, useState, type FormEvent } from 'react';
import { Button, Note, Skeleton, TextInput, useAsync } from './ui';
import { explain, getLocation, putLocation, type HomeLocation } from './weatherApi';

export default function WeatherLocationPanel() {
  const current = useAsync<HomeLocation | null>(getLocation);

  return (
    <section className="rounded-card border border-border bg-surface p-5 sm:p-6">
      <header>
        <h2 className="font-display text-lg font-semibold text-ink">Weather location</h2>
        <p className="mt-1 text-sm text-ink-soft">
          Set where “home” is and the forecast shows up on the family hub.
        </p>
      </header>

      <div className="mt-5">
        {current.loading && (
          <div className="space-y-3" role="status" aria-live="polite">
            <span className="sr-only">Loading the saved location…</span>
            <Skeleton className="h-11 w-full rounded-xl" />
            <Skeleton className="h-11 w-full rounded-xl" />
          </div>
        )}

        {!current.loading && current.error !== null && (
          <div className="space-y-3">
            <Note tone="warn" role="alert">
              {explain(current.error, 'Couldn’t load the saved location.')}
            </Note>
            <Button onClick={current.reload}>Try again</Button>
          </div>
        )}

        {!current.loading && current.error === null && (
          <Form
            initial={current.data}
            onSaved={(loc) => current.set(loc)}
          />
        )}
      </div>
    </section>
  );
}

function Form({
  initial,
  onSaved,
}: {
  initial: HomeLocation | null;
  onSaved: (loc: HomeLocation) => void;
}) {
  const labelId = useId();
  const latId = useId();
  const lngId = useId();

  const [label, setLabel] = useState(initial?.label ?? '');
  const [lat, setLat] = useState(initial ? String(initial.lat) : '');
  const [lng, setLng] = useState(initial ? String(initial.lng) : '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<HomeLocation | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(null);

    const cleanLabel = label.trim();
    const latN = Number(lat.trim());
    const lngN = Number(lng.trim());

    if (!cleanLabel) return setError('Give this place a name, like “Home”.');
    if (!Number.isFinite(latN) || latN < -90 || latN > 90)
      return setError('Latitude must be a number between -90 and 90.');
    if (!Number.isFinite(lngN) || lngN < -180 || lngN > 180)
      return setError('Longitude must be a number between -180 and 180.');

    setSaving(true);
    try {
      // Never lose what was typed: on failure the catch leaves every field exactly as-is.
      const loc = await putLocation({ lat: latN, lng: lngN, label: cleanLabel });
      setSaved(loc);
      setLabel(loc.label);
      setLat(String(loc.lat));
      setLng(String(loc.lng));
      onSaved(loc);
    } catch (err) {
      setError(explain(err, 'That didn’t save. Try again in a moment.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div>
        <label htmlFor={labelId} className="mb-1 block text-sm font-medium text-ink">
          Name
        </label>
        <TextInput
          id={labelId}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Home"
          maxLength={80}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={latId} className="mb-1 block text-sm font-medium text-ink">
            Latitude
          </label>
          <TextInput
            id={latId}
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="35.2271"
            // decimal keypad has no minus; use text so a negative coord is typable
            inputMode="text"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
          />
        </div>
        <div>
          <label htmlFor={lngId} className="mb-1 block text-sm font-medium text-ink">
            Longitude
          </label>
          <TextInput
            id={lngId}
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="-80.8431"
            inputMode="text"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
          />
        </div>
      </div>

      <p className="text-xs leading-relaxed text-ink-faint">
        In a maps app, press and hold your house — the coordinates come up as two decimal
        numbers, latitude then longitude. In the US the longitude is negative — keep the minus.
      </p>

      {error && (
        <Note tone="warn" role="alert">
          {error}
        </Note>
      )}
      {saved && (
        <Note tone="good" role="status">
          Saved. The hub now shows the forecast for {saved.label}.
        </Note>
      )}

      <Button type="submit" variant="primary" disabled={saving}>
        {saving ? 'Saving…' : 'Save location'}
      </Button>
    </form>
  );
}
